/*
 * <nl-wishlist>: wishlist page (sections/nl-wishlist.liquid).
 * Reads product handles from NL.wishlist (localStorage, newest first) or from a shared link (?produkty=a,b) and renders
 * one row per product through the Section Rendering API ({root}products/{handle}?section_id=nl-wishlist-row), four
 * requests at a time, keeping the saved order. Rows stay in sync with wishlist:changed (hearts elsewhere on the page,
 * other tabs). Remove collapses the row, "Přidat vše do košíku" adds every in stock single variant product, the share
 * row builds a link to this page with the handles. Depends on assets/nl-app.js. Respects prefers-reduced-motion.
 */
(() => {
  'use strict';

  if (customElements.get('nl-wishlist')) return;

  const HANDLE = /^[a-z0-9À-ɏ][a-z0-9À-ɏ_-]*$/i;
  const MAX_SHARED = 50;
  const CONCURRENCY = 4;

  const reduced = () =>
    window.NL && window.NL.motion
      ? window.NL.motion.reduced
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class NLWishlist extends HTMLElement {
    connectedCallback() {
      if (this._bound || !window.NL || !window.NL.wishlist) return;
      this._bound = true;

      this.list = this.querySelector('[data-wishlist-list]');
      this.countText = document.querySelector('[data-wishlist-count-text]');
      this.sharedBanner = this.querySelector('[data-wishlist-shared]');
      this.addAll = this.querySelector('[data-wishlist-add-all]');
      this.share = this.querySelector('[data-wishlist-share]');
      this.cache = new Map();
      this.queue = [];
      this.active = 0;

      const root = this.dataset.root || '/';
      this.productBase = `${root.endsWith('/') ? root : `${root}/`}products/`;
      this.sharedHandles = this.readShared();

      this.addEventListener('click', (event) => this.onClick(event));
      this.onChange = () => {
        if (!this.sharedHandles) this.sync();
      };
      document.addEventListener('wishlist:changed', this.onChange);

      if (this.sharedHandles && this.sharedBanner) this.sharedBanner.hidden = false;
      if (this.share && this.sharedHandles) this.share.hidden = true;
      const native = this.querySelector('[data-share-native]');
      if (native && navigator.share) native.hidden = false;

      this.clearPlaceholders();
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:changed', this.onChange);
      this._bound = false;
    }

    /* ---- Data ---- */
    readShared() {
      const param = this.dataset.shareParam || 'produkty';
      const raw = new URLSearchParams(window.location.search).get(param);
      if (!raw) return null;
      const handles = [...new Set(raw.split(',').map((h) => h.trim().toLowerCase()).filter((h) => HANDLE.test(h)))];
      return handles.slice(0, MAX_SHARED);
    }

    get handles() {
      return this.sharedHandles || window.NL.wishlist.get();
    }

    rows() {
      return Array.from(this.list.querySelectorAll(':scope > [data-handle]'));
    }

    /* ---- Render ---- */
    clearPlaceholders() {
      this.list.querySelectorAll('[data-wishlist-placeholder]').forEach((el) => el.remove());
    }

    sync() {
      const handles = this.handles;
      const existing = new Map(this.rows().map((row) => [row.dataset.handle, row]));

      existing.forEach((row, handle) => {
        if (!handles.includes(handle) && !row.dataset.leaving) row.remove();
      });

      let previous = null;
      const jobs = [];
      handles.forEach((handle, index) => {
        let row = existing.get(handle);
        if (!row || !row.isConnected) {
          row = this.placeholder(handle);
          jobs.push({ handle, slot: row, index });
        }
        const anchor = previous ? previous.nextElementSibling : this.list.firstElementChild;
        if (row !== anchor) this.list.insertBefore(row, anchor);
        previous = row;
      });

      this.updateState();
      jobs.forEach((job) => this.enqueue(job.handle, job.slot, job.index));
    }

    placeholder(handle) {
      const li = document.createElement('li');
      li.className = 'nl-wl-row nl-wl-row--skeleton';
      li.dataset.handle = handle;
      li.dataset.pending = '1';
      li.setAttribute('aria-hidden', 'true');
      li.innerHTML =
        '<span class="nl-wl-row__media nl-skeleton"></span>' +
        '<span class="nl-wl-row__info"><span class="nl-skeleton nl-skeleton--title"></span>' +
        '<span class="nl-skeleton nl-skeleton--text nl-wl-row__sk-short"></span></span>' +
        '<span class="nl-wl-row__price"><span class="nl-skeleton nl-skeleton--text nl-wl-row__sk-price"></span></span>' +
        '<span class="nl-wl-row__stock"><span class="nl-skeleton nl-wl-row__sk-badge"></span></span>' +
        '<span class="nl-wl-row__action"><span class="nl-skeleton nl-wl-row__sk-btn"></span></span>';
      return li;
    }

    enqueue(handle, slot, index) {
      this.queue.push({ handle, slot, index });
      this.pump();
    }

    pump() {
      while (this.active < CONCURRENCY && this.queue.length) {
        const job = this.queue.shift();
        if (!job.slot.isConnected) continue;
        this.active += 1;
        this.fetchRow(job.handle)
          .then((row) => this.fill(job, row))
          .finally(() => {
            this.active -= 1;
            this.pump();
            this.updateState();
          });
      }
    }

    async fetchRow(handle) {
      if (this.cache.has(handle)) return this.cache.get(handle).cloneNode(true);
      const url = `${this.productBase}${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
        this.dataset.rowSection || 'nl-wishlist-row'
      )}`;
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (response.status === 404) return 'missing';
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const row = doc.querySelector('[data-wishlist-row]');
        if (!row) return 'missing';
        this.cache.set(handle, row);
        return row.cloneNode(true);
      } catch (error) {
        return null;
      }
    }

    fill(job, row) {
      const { handle, slot, index } = job;
      if (!slot.isConnected) return;
      if (row === 'missing') {
        slot.remove();
        if (!this.sharedHandles) window.NL.wishlist.remove(handle);
        else this.sharedHandles = this.sharedHandles.filter((h) => h !== handle);
        return;
      }
      if (!row) {
        slot.remove();
        return;
      }
      const fresh = document.importNode(row, true);
      if (this.sharedHandles) {
        const remove = fresh.querySelector('[data-wishlist-remove]');
        if (remove) remove.hidden = true;
      }
      if (!reduced()) {
        fresh.classList.add('is-entering');
        fresh.style.setProperty('--wl-index', String(index));
        fresh.addEventListener('animationend', () => fresh.classList.remove('is-entering'), { once: true });
      }
      slot.replaceWith(fresh);
    }

    updateState() {
      const pending = this.list.querySelector('[data-pending]');
      const loaded = this.rows().filter((row) => !row.dataset.pending && !row.dataset.leaving);

      let state = 'ready';
      if (!pending && !loaded.length) state = 'empty';
      else if (!loaded.length) state = 'loading';
      this.dataset.state = state;
      this.list.setAttribute('aria-busy', String(Boolean(pending)));

      if (this.countText) {
        this.countText.textContent = state === 'empty' || state === 'loading' ? '' : this.plural(loaded.length);
      }

      if (this.addAll) {
        const addable = loaded.filter((row) => row.dataset.variantId);
        this.addAll.hidden = addable.length < 2;
      }
    }

    plural(count) {
      let rule = 'other';
      try {
        rule = new Intl.PluralRules(document.documentElement.lang || 'cs').select(count);
      } catch (e) {
        rule = count === 1 ? 'one' : 'other';
      }
      const key = `count${rule.charAt(0).toUpperCase()}${rule.slice(1)}`;
      return String(this.dataset[key] || this.dataset.countOther || '').replace('%count%', String(count));
    }

    /* ---- Actions ---- */
    onClick(event) {
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.removeRow(remove.closest('[data-handle]'));
        return;
      }
      const shareControl = event.target.closest('[data-share]');
      if (shareControl) {
        this.onShare(event, shareControl);
        return;
      }
      if (event.target.closest('[data-wishlist-add-all]')) {
        this.addAllToCart(event.target.closest('[data-wishlist-add-all]'));
        return;
      }
      if (event.target.closest('[data-wishlist-save-shared]')) this.saveShared();
    }

    removeRow(row) {
      if (!row) return;
      const handle = row.dataset.handle;
      if (row.dataset.leaving) return;
      row.dataset.leaving = '1';
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        row.remove();
        window.NL.wishlist.remove(handle);
        const next = this.list.querySelector('[data-wishlist-remove]');
        if (next) next.focus({ preventScroll: true });
        if (window.NL.toast) window.NL.toast(this.dataset.removed, { variant: 'info', timeout: 2600 });
      };
      if (reduced() || !row.animate) {
        done();
        return;
      }
      const height = row.getBoundingClientRect().height;
      row.style.overflow = 'hidden';
      const animation = row.animate(
        [
          { opacity: 1, height: `${height}px` },
          { opacity: 0, height: '0px', paddingTop: '0px', paddingBottom: '0px' },
        ],
        { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }
      );
      animation.onfinish = done;
      window.setTimeout(done, 480); /* hidden tabs may pause animations */
    }

    async addAllToCart(button) {
      const items = this.rows()
        .filter((row) => row.dataset.variantId && !row.dataset.leaving)
        .map((row) => ({ id: row.dataset.variantId, quantity: 1 }));
      if (!items.length || !window.NL.cart) return;
      button.setAttribute('aria-busy', 'true');
      try {
        await window.NL.cart.add(items, { source: 'wishlist', opener: button });
      } catch (error) {
        if (window.NL.toast) window.NL.toast(error.message || this.dataset.error, { variant: 'error' });
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    saveShared() {
      if (!this.sharedHandles) return;
      const own = window.NL.wishlist.get();
      [...this.sharedHandles].reverse().forEach((handle) => {
        if (!own.includes(handle)) window.NL.wishlist.add(handle);
      });
      this.sharedHandles = null;
      if (this.sharedBanner) this.sharedBanner.hidden = true;
      if (this.share) this.share.hidden = false;
      this.rows().forEach((row) => {
        const remove = row.querySelector('[data-wishlist-remove]');
        if (remove) remove.hidden = false;
      });
      const url = new URL(window.location.href);
      url.searchParams.delete(this.dataset.shareParam || 'produkty');
      window.history.replaceState(window.history.state, '', url.toString());
      this.sync();
      if (window.NL.toast && window.NL.strings) {
        window.NL.toast(window.NL.strings.wishlistAdded, { variant: 'success', timeout: 2600 });
      }
    }

    shareUrl() {
      const base = `${window.location.origin}${window.location.pathname}`;
      const handles = this.handles.map((h) => encodeURIComponent(h));
      return handles.length ? `${base}?${this.dataset.shareParam || 'produkty'}=${handles.join(',')}` : base;
    }

    async onShare(event, control) {
      const type = control.dataset.share;
      const link = this.shareUrl();
      const title = this.dataset.shareTitle || document.title;
      const encoded = encodeURIComponent(link);
      const text = encodeURIComponent(title);
      const targets = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
        x: `https://x.com/intent/tweet?url=${encoded}&text=${text}`,
        pinterest: `https://www.pinterest.com/pin/create/button/?url=${encoded}&description=${text}`,
        email: `mailto:?subject=${text}&body=${encoded}`,
      };
      if (targets[type]) {
        control.setAttribute('href', targets[type]);
        return; /* let the link open */
      }
      event.preventDefault();
      if (type === 'native' && navigator.share) {
        try {
          await navigator.share({ title, url: link });
        } catch (error) {
          /* dismissed */
        }
        return;
      }
      if (type === 'copy') {
        try {
          await navigator.clipboard.writeText(link);
          if (window.NL.toast) window.NL.toast(this.dataset.copied, { variant: 'success', timeout: 2400 });
        } catch (error) {
          window.prompt('', link);
        }
      }
    }
  }

  customElements.define('nl-wishlist', NLWishlist);
})();
